import { useEffect, useState } from 'react';
import type { PlantillaMarketing, Regla } from '../../api/ops/campanas';
import { Boton } from '../../componentes/Boton';
import { Chip, type EstadoChip } from '../../componentes/Chip';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Tabla } from '../../componentes/Tabla';
import { estadoDeError } from '../../logica/estados';
import { diaMes } from '../../logica/fechasCortas';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsApartado } from './Campanas';
import { AYUDA, SUBTITULO, ESTADO_PLANTILLA, RUTA_PLANTILLAS_META } from './reglas';

/**
 * 12.6 Plantillas de marketing: solo WhatsApp (G448), Aprobadas · En revisión · Con problemas · Retiradas, con su texto
 * (`body` de listMarketingTemplates, G745). «En revisión» sale de listMetaTemplates (categoría marketing, pendientes) y
 * solo para quien gestiona las plantillas en Canales. Crear, corregir y retirar llevan a Canales › Plantillas de Meta
 * (11.4, G275). El Encargado ve la lista y abre la ficha en solo lectura con el texto.
 */
type Vista = 'aprobadas' | 'revision' | 'problemas' | 'retiradas';
interface Fila {
    id: string;
    nombre: string;
    idioma: string;
    cuerpo: string | null;
    estado: { texto: string; chip: EstadoChip | null };
    motivo: string | null;
    fecha: string | undefined;
}

const VISTAS: readonly { valor: Vista; etiqueta: string }[] = [
    { valor: 'aprobadas', etiqueta: 'Aprobadas' },
    { valor: 'revision', etiqueta: 'En revisión' },
    { valor: 'problemas', etiqueta: 'Con problemas' },
    { valor: 'retiradas', etiqueta: 'Retiradas' },
];

const VACIA: Readonly<Record<Vista, [string, string]>> = {
    aprobadas: ['Todavía no hay plantillas aprobadas', 'Una plantilla es un mensaje de WhatsApp que Meta revisa una vez y luego usas en campañas y automáticos.'],
    revision: ['Nada en revisión', 'Las que envíes a Meta esperan aquí hasta que las apruebe o las rechace.'],
    problemas: ['Ninguna con problemas', 'Aquí verás las rechazadas o pausadas por Meta, con su motivo.'],
    retiradas: ['No hay plantillas retiradas', 'Las que retires de Meta se quedan aquí, con su historial.'],
};

function fila(p: PlantillaMarketing): Fila {
    return { id: p.template_id, nombre: p.name, idioma: p.locale, cuerpo: p.body ?? null, estado: ESTADO_PLANTILLA[p.status], motivo: p.status_reason ?? null, fecha: p.status_at };
}

export function Plantillas(props: PropsApartado) {
    const { ops, negocio, movil, soloLectura, navegar, abrirAjustes } = props;
    const { puede } = usePermisos();
    const enCanales = puede('canales.plantillas.gestionar');
    const vistas = VISTAS.filter((v) => v.valor !== 'revision' || enCanales);
    const [vista, setVista] = useState<Vista>('aprobadas');
    const [marketing, setMarketing] = useState<PlantillaMarketing[] | null>(null);
    const [revision, setRevision] = useState<Fila[]>([]);
    const [reglas, setReglas] = useState<Regla[]>([]);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [abierta, setAbierta] = useState<Fila | null>(null);

    useCabecera({
        ayuda: AYUDA.plantillas,
        subtitulo: SUBTITULO.equipo,
        acciones: [
            ...(!movil ? [{ clave: 'ajustes', texto: 'Ajustes', icono: 'sliders-horizontal' as const, secundaria: true, onClick: abrirAjustes }] : []),
            ...(enCanales && !soloLectura ? [{ clave: 'nueva', texto: 'Nueva plantilla', icono: 'plus' as const, onClick: () => { navegar(RUTA_PLANTILLAS_META); } }] : []),
        ],
    });

    useEffect(() => {
        let vivo = true;
        ops.listMarketingTemplates(negocio, { limit: 100 })
            .then((r) => {
                if (vivo) {
                    setMarketing(r.data);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        ops.listAutomationRules(negocio, { limit: 100 })
            .then((r) => {
                if (vivo) {
                    setReglas(r.data);
                }
            })
            .catch(() => undefined);
        if (enCanales) {
            ops.listMetaTemplates(negocio, { category: 'marketing', status: 'pending', limit: 100 })
                .then((r) => {
                    if (vivo) {
                        setRevision(r.data.map((p) => ({ id: p.id, nombre: p.name, idioma: p.locale, cuerpo: (p.components ?? []).find((c) => c.type === 'body')?.text ?? null, estado: { texto: 'En revisión', chip: 'pending' }, motivo: null, fecha: p.submitted_at })));
                    }
                })
                .catch(() => undefined);
        }
        return () => {
            vivo = false;
        };
    }, [ops, negocio, enCanales, intento]);

    if (error !== null) {
        return <EstadoError seccion="las plantillas" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={movil} />;
    }
    if (marketing === null) {
        return <EstadoCargando seccion="las plantillas" />;
    }
    const filas: Fila[] =
        vista === 'revision'
            ? revision
            : marketing.filter((p) => (vista === 'aprobadas' ? p.status === 'approved' : vista === 'problemas' ? p.status === 'paused' || p.status === 'rejected' : p.status === 'withdrawn')).map(fila);
    const usos = abierta === null ? [] : reglas.filter((r) => r.current_version.content.meta_template_id === abierta.id && r.status !== 'archived');
    const irACanales = (id?: string) => {
        navegar(id === undefined ? RUTA_PLANTILLAS_META : `${RUTA_PLANTILLAS_META}&plantilla=${encodeURIComponent(id)}`);
    };

    return (
        <div className="rv-ca-apartado">
            {!enCanales && (
                <p className="rv-ca-nota">
                    <Icono nombre="eye" />
                    <span>Con tu rol ves las plantillas y su estado. Crearlas o retirarlas lo hacen el propietario y los administradores, en Canales.</span>
                </p>
            )}
            <Filtros etiqueta="Estado en Meta" items={vistas.map((v) => ({ etiqueta: v.etiqueta }))} valor={Math.max(0, vistas.findIndex((v) => v.valor === vista))} onCambio={(i) => { setVista(vistas[i]?.valor ?? 'aprobadas'); }} />
            {filas.length === 0 ? (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa">
                        <Icono nombre="message-square-text" />
                    </span>
                    <strong>{VACIA[vista][0]}</strong>
                    <span>{VACIA[vista][1]}</span>
                    {vista === 'aprobadas' && enCanales && !soloLectura && (
                        <Boton icono="plus" movil={movil} onClick={() => { irACanales(); }}>
                            Nueva plantilla
                        </Boton>
                    )}
                </div>
            ) : (
                <Tabla
                    etiqueta="Plantillas de marketing"
                    movil={movil}
                    columnas={[
                        { clave: 'plantilla', titulo: 'Plantilla', ancho: 'minmax(240px, 2fr)' },
                        { clave: 'idioma', titulo: 'Idioma', ancho: 'minmax(80px, 0.5fr)' },
                        { clave: 'estado', titulo: 'Estado en Meta', ancho: 'minmax(130px, 0.9fr)' },
                    ]}
                    filas={filas.map((p) => ({
                        clave: p.id,
                        etiqueta: p.nombre,
                        onAbrir: () => { setAbierta(p); },
                        celdas: {
                            plantilla: (
                                <span className="rv-en-celda">
                                    <strong>{p.nombre}</strong>
                                    {p.cuerpo !== null && <small className="rv-cn-extracto">{p.cuerpo}</small>}
                                </span>
                            ),
                            idioma: p.idioma,
                            estado: p.estado.chip === null ? p.estado.texto : <Chip estado={p.estado.chip}>{p.estado.texto}</Chip>,
                        },
                    }))}
                />
            )}
            {abierta !== null && (
                <PanelLateral abierto onCerrar={() => { setAbierta(null); }} movil={movil} tamano="ficha" titulo={abierta.nombre} eyebrow="Plantilla de marketing · WhatsApp" subtitulo={abierta.idioma} {...(abierta.estado.chip === null ? {} : { chip: abierta.estado.texto, chipEstado: abierta.estado.chip })} icono="message-square-text">
                    <div className="rv-cn-ficha">
                        {abierta.motivo !== null && (
                            <div className="rv-cn-alerta rv-cn-alerta--amarilla" role="alert">
                                <Icono nombre="triangle-alert" />
                                <span>{abierta.motivo}</span>
                            </div>
                        )}
                        <div className="rv-cp-seccion">
                            <h3>Así la recibe el cliente</h3>
                            <div className="rv-cp-muestra">{abierta.cuerpo ?? 'El texto de esta plantilla llegará cuando Meta la vuelva a aprobar.'}</div>
                        </div>
                        <div className="rv-cn-hechos">
                            <div className="rv-cn-hecho">
                                <span>Estado en Meta</span>
                                <strong>{abierta.estado.texto}</strong>
                                {abierta.fecha !== undefined && <small>{`Desde el ${diaMes(abierta.fecha)}`}</small>}
                            </div>
                        </div>
                        <div className="rv-cp-seccion">
                            <h3>Dónde se usa</h3>
                            {usos.length === 0 ? <p>En ningún mensaje automático.</p> : (
                                <ul className="rv-cp-lista">
                                    {usos.map((r) => (
                                        <li key={r.id}>
                                            <span>
                                                <Icono nombre="zap" /> {r.name}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        {enCanales && (
                            <div className="rv-cp-franja__botones">
                                <Boton tipo="secundario" icono="arrow-right" movil={movil} onClick={() => { irACanales(abierta.id); }}>
                                    {abierta.estado.texto === 'Aprobada' ? 'Crear otra en Canales' : 'Ver en Canales'}
                                </Boton>
                                {abierta.estado.texto !== 'Retirada' && (
                                    <Boton tipo="secundario" rojo icono="arrow-right" movil={movil} onClick={() => { irACanales(abierta.id); }}>
                                        Retirar en Canales
                                    </Boton>
                                )}
                            </div>
                        )}
                    </div>
                </PanelLateral>
            )}
        </div>
    );
}
